import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { handleResponse } from '@/api/handleResponse';
import type { User } from '@/api/user';
import type { FundmentClientOverview } from '@shared/types/FundmentClient';

export type { FundmentClientOverview };

/**
 * Runtime enum re-declared here (the original lives in integration.server.ts,
 * which imports server-only modules and must not enter the browser bundle).
 */
export enum IntegrationStatus {
    ENABLED = 'ENABLED',
    DISABLED = 'DISABLED',
    FAILED = 'FAILED',
    UNSET = 'UNSET',
}

export type AddeparIntegrationType = {
    addeparIntegrationId: string;
    user: User;
    status: IntegrationStatus;
};

export type FundmentIntegrationType = {
    fundmentIntegrationId: string;
    status: IntegrationStatus;
};

export type AddeparClient = {
    addeparClientId: number;
    name: string;
};

/* ------------------------------------------------------------------ Addepar */

/** Browser port of `fetchAddeparIntegrationForUser`. */
export async function fetchAddeparIntegration() {
    const response = await apiFetch('/integrations/addepar', { method: 'GET' });
    return handleResponse<AddeparIntegrationType>(
        response,
        'GET /integrations/addepar'
    );
}

/** Browser port of `createAddeparIntegration` (exchanges the OAuth code). */
export async function createAddeparIntegration(code: string) {
    const response = await apiFetch('/integrations/addepar', {
        method: 'POST',
        body: { code },
    });
    return handleResponse<AddeparIntegrationType>(
        response,
        'POST /integrations/addepar'
    );
}

/** Browser port of `fetchAddeparClientsForIntegration`. */
export async function fetchAddeparClients() {
    const response = await apiFetch('/integrations/addepar/clients', {
        method: 'GET',
    });
    return handleResponse<AddeparClient[]>(
        response,
        'GET /integrations/addepar/clients'
    );
}

/** Browser port of `disableAddeparIntegration`. */
export async function disableAddeparIntegration(deleteClientData: boolean) {
    const query = deleteClientData ? '?deleteClientData=true' : '';
    const response = await apiFetch(`/integrations/addepar${query}`, {
        method: 'DELETE',
    });
    return handleResponse<unknown>(
        response,
        `DELETE /integrations/addepar${query}`
    );
}

export const addeparIntegrationQueryKey = ['integration', 'addepar'] as const;
export const addeparClientsQueryKey = [
    'integration',
    'addepar',
    'clients',
] as const;

export function useAddeparIntegration() {
    return useQuery({
        queryKey: addeparIntegrationQueryKey,
        queryFn: async () => {
            const { data } = await fetchAddeparIntegration();
            // Absence of an integration is not an error for the dashboard.
            return data ?? null;
        },
    });
}

/* ----------------------------------------------------------------- Fundment */

/** Browser port of `fetchFundmentIntegrationForUser`. */
export async function fetchFundmentIntegration() {
    const response = await apiFetch('/integrations/fundment', {
        method: 'GET',
    });
    return handleResponse<FundmentIntegrationType>(
        response,
        'GET /integrations/fundment'
    );
}

/** Browser port of `createFundmentIntegration` (exchanges the OAuth code). */
export async function createFundmentIntegration(code: string) {
    const response = await apiFetch('/integrations/fundment', {
        method: 'POST',
        body: { code },
    });
    return handleResponse<FundmentIntegrationType>(
        response,
        'POST /integrations/fundment'
    );
}

/** Browser port of `disableFundmentIntegration`. */
export async function disableFundmentIntegration() {
    const response = await apiFetch('/integrations/fundment', {
        method: 'DELETE',
    });
    return handleResponse<unknown>(response, 'DELETE /integrations/fundment');
}

export const fundmentIntegrationQueryKey = [
    'integration',
    'fundment',
] as const;

export type FundmentClientsError =
    | 'FUNDMENT_INTEGRATION_RECONNECTION_REQUIRED'
    | 'SERVER_ERROR';

/**
 * Browser port of `fetchFundmentClients`. A 424 from the backend means the
 * Fundment integration needs reconnecting (distinct from a generic error).
 */
export async function fetchFundmentClients(): Promise<
    | { data: FundmentClientOverview[]; error: null }
    | { data: null; error: FundmentClientsError }
> {
    const response = await apiFetch('/integrations/fundment/clients', {
        method: 'GET',
    });

    if (response.status === 424) {
        return {
            data: null,
            error: 'FUNDMENT_INTEGRATION_RECONNECTION_REQUIRED',
        };
    }

    const { data, error } = await handleResponse<FundmentClientOverview[]>(
        response,
        'GET /integrations/fundment/clients'
    );
    if (error) return { data: null, error: 'SERVER_ERROR' };
    return { data: data ?? [], error: null };
}

export const fundmentClientsQueryKey = [
    'integration',
    'fundment',
    'clients',
] as const;
